<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"

defineOptions({
    name: "OptimDashboardExportDialog",
})

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    header: string
    message: string
    confirmLabel: string
    cancelLabel: string
    choiceC5: string
    choiceC4: string
    choiceC2: string
    /** Valeurs par défaut à réappliquer à l’ouverture. */
    defaultSegments: [boolean, boolean, boolean]
}>()

const visible = defineModel<boolean>("visible", { required: true })

const emit = defineEmits<{
    confirm: [payload: { c5: boolean; c4: boolean; c2: boolean }]
}>()

// ================================
// STATE
// ================================
/** Case à cocher : inclure les analyses segment C5 dans l’export. */
const segmentC5 = ref(props.defaultSegments[0])

/** Case à cocher : inclure les analyses segment C4 dans l’export. */
const segmentC4 = ref(props.defaultSegments[1])

/** Case à cocher : inclure les analyses segment C2 dans l’export. */
const segmentC2 = ref(props.defaultSegments[2])

// ================================
// COMPUTED
// ================================
/** Désactive la validation tant qu’aucun segment n’est sélectionné. */
const getConfirmDisabled = computed(
    () => !segmentC5.value && !segmentC4.value && !segmentC2.value
)

// ================================
// WATCHERS
// ================================
/**
 * Réinitialise les cases à l’ouverture du dialogue (segments par défaut du parent).
 */
watch(
    () => visible.value,
    (open) => {
        if (open) {
            segmentC5.value = props.defaultSegments[0]
            segmentC4.value = props.defaultSegments[1]
            segmentC2.value = props.defaultSegments[2]
        }
    }
)

/**
 * Met à jour les cases si les défauts parent changent pendant que le dialogue est ouvert.
 */
watch(
    () => props.defaultSegments,
    (v) => {
        if (visible.value) {
            segmentC5.value = v[0]
            segmentC4.value = v[1]
            segmentC2.value = v[2]
        }
    },
    { deep: true }
)

// ================================
// INTERACTIONS
// ================================
function onClickCancel(): void {
    visible.value = false
}

function onClickConfirm(): void {
    emit("confirm", {
        c5: segmentC5.value,
        c4: segmentC4.value,
        c2: segmentC2.value,
    })
    visible.value = false
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL — export segments -->
    <!-- ================================ -->
    <BaseDialog
        v-model:visible="visible"
        modal
        :header="header"
        class="optim-dashboard-export-dialog w-full max-w-lg"
        :pt="{
            content: 'flex flex-col gap-4',
        }"
    >
        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL — choix segments -->
        <!-- ================================ -->
        <p class="mb-1 text-center text-sm text-surface-700">
            {{ message }}
        </p>
        <div class="flex flex-col gap-3">
            <div class="flex items-center gap-2">
                <BaseCheckbox
                    v-model="segmentC5"
                    input-id="optim-export-c5"
                    binary
                />
                <label
                    for="optim-export-c5"
                    class="text-sm text-surface-800 cursor-pointer"
                >
                    {{ choiceC5 }}
                </label>
            </div>
            <div class="flex items-center gap-2">
                <BaseCheckbox
                    v-model="segmentC4"
                    input-id="optim-export-c4"
                    binary
                />
                <label
                    for="optim-export-c4"
                    class="text-sm text-surface-800 cursor-pointer"
                >
                    {{ choiceC4 }}
                </label>
            </div>
            <div class="flex items-center gap-2">
                <BaseCheckbox
                    v-model="segmentC2"
                    input-id="optim-export-c2"
                    binary
                />
                <label
                    for="optim-export-c2"
                    class="text-sm text-surface-800 cursor-pointer"
                >
                    {{ choiceC2 }}
                </label>
            </div>
        </div>
        <template #footer>
            <!-- ================================ -->
            <!-- FOOTER / ACTIONS -->
            <!-- ================================ -->
            <div class="flex w-full justify-end gap-2">
                <BaseButton
                    :label="cancelLabel"
                    severity="secondary"
                    @click="onClickCancel"
                />
                <BaseButton
                    :label="confirmLabel"
                    :disabled="getConfirmDisabled"
                    @click="onClickConfirm"
                />
            </div>
        </template>
    </BaseDialog>
</template>
